/**
 * axios-config.js
 */

import React from 'react';
import axios from "axios";
import { formatISO } from 'date-fns';
import { useSnackbar } from 'notistack';
import * as Constants from "utils/constants";
import { shouldRenderMobileLayout } from 'utils/app-context';
import logger from 'utils/logger';
import { UserContext } from 'contexts/user/user-context';
import { AuthContext } from 'contexts/auth/auth-context';
import NetworkConnectionContext from 'contexts/network-connection/network-connection.context';
import Button from 'components/button';


export default function AxiosConfig(props) {
  const [state, setState] = React.useState({});
  const { isAuthenticated, setIsAuthenticated } = React.useContext(AuthContext);
  const { isConnected, setIsConnected } = React.useContext(NetworkConnectionContext);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { user, setUser } = React.useContext(UserContext);
  const initialPtApiVersion = user?.initialPtApiVersion;
  const timeoutToastKey = React.useRef();


  const getErrorMessage = (error) => {
    return error?.response?.data?.Message
      || error?.response?.data
      || error?.response?.statusText
      || error?.message
      || 'An unknown error occurred';



    // if (error.response) {
    //   if (error.response.data && typeof error.response.data.Message === "string") {
    //     return `${error.response.data.Message}`;
    //   } else if (error.response.data && typeof error.response.data === "string") {
    //     return `${error.response.data}`;
    //   } else {
    //     return `${error.response.statusText}`;
    //   }
    // } else if (error.message) {
    //   // this error is common and usually due to transient network connectivity
    //   // such as when a computer is brought out of sleep mode
    //   if (error.message === "Network Error") {
    //     return "Connection Lost. Network will reconnect automatically when available.";
    //   } else {
    //     return error.message;
    //   }
    // } else {
    //   return "An unknown error occurred";
    // }
  }


  function isDict(v) {
    return typeof v ==='object' && v !== null && !(v instanceof Array) && !(v instanceof Date);
  }


  // iterates through object looking for date objects to be sent to server
  // if found we convert them to a timezone-aware date string
  const parseDatesOut = React.useCallback((obj) => {
    if (isDict(obj)) {
      for (let key in obj) {
        if (isDict(obj[key])) {
          parseDatesOut(obj[key]);
        } else if (obj[key] && typeof obj[key].getMonth === 'function') {
          obj[key] = formatISO(obj[key]);
        }
      }
    }
    return obj;
  }, []);


  // axios config
  React.useEffect(() => {
    axios.defaults.baseURL = process.env.REACT_APP_API_HOST;
    axios.defaults.withCredentials = true;
    axios.defaults.timeout = 60 * 1000; // 60 seconds
    axios.defaults.headers.post["Content-Type"] = "application/json";

    // include saved CSRF token in every request
    const csrfInterceptor = axios.interceptors.request.use(config => {
      let csrfToken = localStorage.getItem(Constants.CSRF_STORAGE_NAME);
      config.headers[Constants.CSRF_HEADER_NAME] = csrfToken;
      return config;
    });

    // request interceptor - converts any dates to include timezone
    const dateInterceptor = axios.interceptors.request.use(
      config => {
        if (config.data) {
          config.data = parseDatesOut(config.data);
        }
        return config;
      }
    );

    // retry request interceptor
    const retryRequestInterceptor = axios.interceptors.request.use(
      config => {
        if (!config.retry) {
          config.retry = {
            count: 0,
            timeStamp: Date.now()
          }
        }
        return config;
      }
    );

    // response values control auth state and params
    const responseInterceptor = axios.interceptors.response.use(
      response => {  
        // set custom success field
        response.isSuccess = response.status >= 200 && response.status < 300;

        // close persisted server connection error
        if (timeoutToastKey.current) {
          closeSnackbar(timeoutToastKey.current);
          timeoutToastKey.current = null;
        }

        // check if we were disconnected and set state accordingly
        if (!isConnected) {
          setIsConnected(true);
        }

        // check if server response references a newer API version
        const apiVer = response.headers[Constants.PTAPI_VERSION_HEADER_NAME];
        if (user && apiVer && apiVer !== user.initialPtApiVersion) {
          enqueueSnackbar("PACKTRACK has been updated! Save any changes and then Reload the page.", {
            variant: "warning",
            persist: true,
            preventDuplicate: true,
            anchorOrigin: {
              vertical: shouldRenderMobileLayout() ? "bottom" : "top",
              horizontal: "center"
            },
            action: key => (
              <Button
                variant="contained"
                size="small"
                style={{margin: '0px 12px', paddingTop: 6}}
                onClick={() => window.location.reload()}>
                RELOAD
              </Button>
            )      
          });
        }

        // check for CSRF response header
        if (response.headers && Constants.CSRF_HEADER_NAME in response.headers) {
          setIsAuthenticated(true);

          // store CSRF token in local storage
          localStorage.setItem(
            Constants.CSRF_STORAGE_NAME,
            response.headers[Constants.CSRF_HEADER_NAME]
          );
        }

        // check for PTUSER response header
        if (response.headers && Constants.PTUSER_HEADER_NAME in response.headers) {
          const userEncoded = response.headers[Constants.PTUSER_HEADER_NAME];
          // const userJson = Buffer.from(userEncoded, 'base64');
          const userJson = window.atob(userEncoded);
          const user = JSON.parse(userJson);
          setUser(user);
        }

        // const data = response.data;
        const url = new URL(response.request.responseURL);
        logger.logNetwork(`[axios-config.js] Completed query to: ${url.pathname + url.search}`, response.data);

        // return data || true;
        return response;
      },
      error => {
        if (error?.response) {
          error.response.isSuccess = false;
        }

        // generate full request url
        const fullUrl = error.config.url.includes('http')
          ? error.config.url
          : `${error.config.baseURL}${error.config.url}`;

        // if not api request then do nothing special and return
        if (!fullUrl.includes(process.env.REACT_APP_API_HOST)) {
          return Promise.reject(error);
        }

        // if 401 response for non-login request then initiate inline auth procedure
        if (error?.response?.status === 401 
          && !error?.response?.config?.url?.includes("account/login")
          && !error?.response?.config?.url?.includes('account/token-login')
        ) {
          // return a promise that is resolved after authentication completes
          // and initial attempted request has been performed again.
          return new Promise((resolve, reject) => {
            // save failed request and promise resolver
            setState({
              request: error.response.config,
              resolve,
            });

            // show auth dialog
            setIsAuthenticated(false);
          });
        } else {
          console.error("Request error", {error});

          // possible network issues if no reponse returned, try again.
          const maxRetries = process.env.NODE_ENV === 'development' ? 0 : 1;
          const config = error.config;
          if (!error.response && config.retry && config.retry.count < maxRetries) {
            config.retry.count += 1;
            return new Promise(resolve => resolve(axios(config)));
          }

          const message = getErrorMessage(error) || 'Network Error';

          // if error.config.bypassErrorHandling
          // skips all internal default error handling and rejects promise.
          // TODO: this can be replaced with onError usage. also entire axios-config needs a refactor/redesign.
          if (error.config.bypassErrorHandling) {
            return Promise.reject({...error, message});
          } else {
            // if error.config.onError, allow it to try to handle error.
            // if returns true, it it will handle itself, if false, continue with default handling.
            if (error.config.onError && error.config.onError(error)) {
              return Promise.resolve({ ...error.response, message });
            }

            if (error.response) {
              enqueueSnackbar(message, {
                variant: "error",
                autoHideDuration: 12000
              });
            } else {
              // if no response returned this means we can't connect to the server
              // and get a response back possibly due to network issues. Axios returns a
              // generic 'Network Error' message in this case.
              if (isConnected) {
                // we're not disconnected from the network but didn't get a response
                console.error('error response', error);
                if (!timeoutToastKey.current) {
                  timeoutToastKey.current = enqueueSnackbar('Unable to connect to server', {
                    variant: 'error',
                    persist: true,
                    preventDuplicate: true,
                    anchorOrigin: {
                      vertical: "top",
                      horizontal: "center"
                    },
                  });
                }
              }
              // else do nothing. The NetworkConnectionProvider will display a connection lost message.
            }

            return Promise.resolve({ ...error.response, message });
          }
        }
      }
    );

    return () => {
      axios.interceptors.request.eject(csrfInterceptor);
      axios.interceptors.request.eject(dateInterceptor);
      axios.interceptors.request.eject(retryRequestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    }
  }, [
    closeSnackbar,
    enqueueSnackbar,
    parseDatesOut,
    setIsAuthenticated,
    isConnected,
    setIsConnected,
    initialPtApiVersion,
    user,
    setUser
  ]);

  // called when authentication state changes
  React.useEffect(() => {
    if (isAuthenticated && state.request && state.resolve) {
      // perform initial request again
      axios(state.request).then(response => {
        state.resolve(response);
        setState({});
      });
    }
  }, [isAuthenticated, state]);

  return null;
}
